import React, { useState, useEffect } from 'react';
import { Paper, Typography, Grid, Chip, Box, Avatar, useTheme, Fade, CircularProgress } from '@mui/material';
import { AccountBalance, Payment } from '@mui/icons-material';
import {
  BalanceInfo,
  CreditWalletSearchFilterList,
  CreditWalletTableActionContainer,
  IconStyle,
  PaymentModeList,
  Transaction,
  TypeList,
  Vendor,
} from '../constants';
import ReusableTable, { Column } from './ReusableTable';
import VendorSelectionModal from './VendorSelectionModal';
import Title from '@Components/Title';
import ExportBtn from '@Components/Button/Export';
import FilterSearch from '@Components/Filter/Search';
import moment from 'moment';
import SbButton from '@Components/Button';
import FilterSelect from '@Components/Filter/Dropdown';
import DateRangePicker from '@Components/DateRangePicker';
import { AmountTypo, CreditedIcon, DebitedIcon, NavigationBtn, TransactionStatus } from '../../Transactions/style';
import { USER_STATUS_COLOR } from '@Constants/status';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import BalanceIcon from '@mui/icons-material/PaymentsOutlined';
import Image from '@Components/Image';
import { getAllReadNotifications, getAllUnreadNotifications } from '@Reducers/Notifications/actions';
import { SuccessErrorModalOpen, SuccessErrorModalReset } from '@Reducers/SuccessErrorModal';
import { toggleBoolean } from '@Reducers/booleanSlice/booleanSlice';
import { downloadCreditTransactionReport } from '@Reducers/PurchaseInvoiceDownloadSlice/downloadCreditTransactionReportSlice';
import Loader from '@Components/Loader';

export const SearchFilterList = [{ label: 'Carrier', value: 'carrier' }];

interface VendorDetailsProps {
  vendors: Vendor[];
  vendor: any;
  onVendorSelect: (vendor: Vendor) => void;
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  loading: boolean;
  balanceLoading: boolean;
  transactions: Transaction[];
  onFiltersChange: (filters: any) => void;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  balance: BalanceInfo;
  creditNoteBalance: number;
}

const VendorDetails: React.FC<VendorDetailsProps> = ({
  vendors,
  vendor,
  onVendorSelect,
  isModalOpen,
  setIsModalOpen,
  loading,
  balanceLoading,
  transactions,
  onFiltersChange,
  onPageChange,
  onRowsPerPageChange,
  page,
  rowsPerPage,
  totalCount,
  balance,
  creditNoteBalance,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const orgCountryCurrency = useSelector(
    (state: RootStateType) => state.orgSetup.organizationData?.currency?.name || '',
  );
  const { limit } = useSelector((state: RootStateType) => state.notifications);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSelect, setFilterSelect] = useState('carrier');
  const [filterSelectValue, setFilterSelectValue] = useState('invoiceNumber');
  const [filterText, setFilterText] = useState('');
  const [status, setStatus] = useState<string[]>([]);
  const [mode, setMode] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(1, 'months').startOf('day').toDate(),
    endDate: moment().endOf('day').toDate(),
  });
  const { loading: creditTransactionReportLoading } = useSelector(
    (state: RootStateType) => state.creditTransactionPurchaseInvoiceReport,
  );

  // Apply filters when they change
  useEffect(() => {
    applyFilters();
  }, [filterText, filterSelectValue, status, mode, dateRange]);

  const applyFilters = () => {
    const filters: any = {
      type: status,
      mode: mode,
      fromDate: moment(dateRange.startDate).format('DD/MM/YYYY'),
      toDate: moment(dateRange.endDate).format('DD/MM/YYYY'),
    };

    // Only add search filter if there's search text
    if (filterText && filterSelectValue) {
      filters[filterSelectValue] = filterText;
    }

    onFiltersChange(filters);
  };

  const onDateChange = ({ startDate, endDate }: { startDate: Date; endDate: Date }, isOpen: boolean) => {
    if (isOpen) return;
    setDateRange({ startDate, endDate });
  };

  const onStatusFilterChange = (selected: Array<string>) => {
    setStatus(selected);
  };

  const onModeFilterChange = (selected: Array<string>) => {
    setMode(selected);
  };

  const handleClearFilters = () => {
    setFilterText('');
    setMode([]);
    setStatus([]);
    setDateRange({
      startDate: moment().subtract(1, 'months').startOf('day').toDate(),
      endDate: moment().endOf('day').toDate(),
    });
    setFilterSelectValue('invoiceNumber');
  };

  // Columns for transaction table
  const columns: Column[] = [
    {
      id: 'sno',
      label: 'S.No.',
      render: (_row: Transaction, index?: number) => index! + 1,
    },
    {
      id: 'createdAt',
      label: 'Date',
      render: (row: Transaction) => moment(row.createdAt).format('DD/MM/YYYY'),
    },
    {
      id: 'description',
      label: 'Description',
      render: (row: Transaction) => row.description || 'No description',
    },
    {
      id: 'type',
      label: 'Transaction Type',
      render: (row: Transaction) => row.type.toUpperCase(),
    },
    {
      id: 'invoiceNumber',
      label: 'Invoice Number',
    },
    {
      id: 'mode',
      label: 'Payment Mode',
      render: (row: Transaction) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          {row.mode === 'debit' ? (
            <DebitedIcon>
              <CallMadeIcon />
            </DebitedIcon>
          ) : (
            <CreditedIcon>
              <CallReceivedIcon />
            </CreditedIcon>
          )}
          {row.mode.charAt(0).toUpperCase() + row.mode.slice(1)}
        </Box>
      ),
    },
    {
      id: 'amount',
      label: 'Amount',
      render: (row: Transaction) => (
        <AmountTypo type={row.type}>
          {row.currency} {parseFloat(row.amount).toLocaleString()}
        </AmountTypo>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (row: Transaction) => (
        <TransactionStatus
          bgcolor={USER_STATUS_COLOR[row.status].bgColor}
          color={USER_STATUS_COLOR[row.status].textColor}
        >
          {USER_STATUS_COLOR[row.status]?.text}
        </TransactionStatus>
      ),
    },
  ];

  const handleDownload = async () => {
    const res = await dispatch(
      downloadCreditTransactionReport({
        currentPage: page,
        perPage: rowsPerPage,
        fromDate: moment(dateRange.startDate).format('DD/MM/YYYY'),
        toDate: moment(dateRange.endDate).format('DD/MM/YYYY'),
        carrierId: vendor?._id || '',
      }),
    );

    if (downloadCreditTransactionReport.fulfilled.match(res)) {
      dispatch(
        getAllUnreadNotifications({
          page: 1,
          limit,
        }),
      );
      dispatch(getAllReadNotifications({ page: 1, limit }));
      dispatch(
        SuccessErrorModalOpen({
          type: 'success',
          title: 'Download in Progress',
          subTitle: 'Your report is being generated. It will be available shortly in the downloads bar',
          btnText: 'Close',
          onConfirm: () => {
            dispatch(toggleBoolean());
            dispatch(SuccessErrorModalReset());
          },
        }),
      );
    }
  };

  return (
    <>
      <Loader loading={creditTransactionReportLoading} overly />
      <Fade in timeout={500}>
        <Box>
          <Title
            title="Credit and Transaction History"
            subTitle="View detailed credit balances, payments, and transaction records for the selected vendor."
            removeBorder
          />

          {/* Header Section */}
          <Paper
            sx={{
              borderRadius: 2,
              mb: 4,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <Box sx={{ p: 4, pb: 3 }}>
              {/* Search Input */}
              <Box sx={{ mb: 3 }}>
                <FilterSearch
                  onSelectChange={(value: string) => {
                    setFilterSelect(value);
                  }}
                  list={SearchFilterList}
                  selectValue={filterSelect}
                  textValue={searchTerm}
                  onTextChange={e => {
                    setSearchTerm(e.target.value);
                  }}
                  onTextSearch={() => {
                    console.info('Search for:', filterSelect, searchTerm);
                    setIsModalOpen(true);
                  }}
                  onTextClear={() => {
                    setSearchTerm('');
                    setIsModalOpen(true);
                  }}
                />
              </Box>

              {/* Vendor Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Avatar
                    sx={{
                      width: 50,
                      height: 50,
                      bgcolor: theme.palette.primary.main,
                      fontSize: '1.8rem',
                      fontWeight: 'bold',
                      mr: 3,
                    }}
                  >
                    {vendor.name.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="500">
                      {vendor.name}
                    </Typography>
                  </Box>
                </Box>

                <ExportBtn
                  iconSrc="/images/file_download.svg"
                  width={25}
                  alt="Export"
                  label="Export"
                  onClick={handleDownload}
                />
              </Box>

              {/* Transaction Metrics Grid */}
              <Grid container spacing={3}>
                {balanceLoading ? (
                  <Grid container justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
                    <CircularProgress color="primary" />
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={12} sm={6} md={3}>
                      <NavigationBtn>
                        <BalanceIcon style={IconStyle} />
                        <div className="ml-4">
                          <Typography className="sm-text">Outstanding Balance({orgCountryCurrency})</Typography>
                          <Typography className="md-text">
                            {(balance.totalInvoicedAmount - Math.abs(balance.totalInvoicedPaidAmount))?.toFixed(2)}
                          </Typography>
                        </div>
                      </NavigationBtn>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <NavigationBtn>
                        <AccountBalance style={IconStyle} />
                        <div className="ml-4">
                          <Typography className="sm-text">Total Invoiced Amount</Typography>
                          <Typography className="md-text">{balance.totalInvoicedAmount?.toFixed(2)}</Typography>
                        </div>
                      </NavigationBtn>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <NavigationBtn>
                        <Payment style={IconStyle} />
                        <div className="ml-4">
                          <Typography className="sm-text">Total Invoiced Paid</Typography>
                          <Typography className="md-text">{balance.totalInvoicedPaidAmount?.toFixed(2)}</Typography>
                        </div>
                      </NavigationBtn>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <NavigationBtn>
                        <Image src="/images/icons/CreditNote.svg" style={IconStyle} alt="Credit Note" />
                        <div className="ml-4">
                          <Typography className="sm-text">Credit Note Balance({orgCountryCurrency})</Typography>
                          <Typography className="md-text">{creditNoteBalance?.toFixed(2)}</Typography>
                        </div>
                      </NavigationBtn>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </Paper>

          {/* Transaction History */}

          <CreditWalletTableActionContainer>
            <FilterSearch
              onSelectChange={(value: string) => {
                setFilterSelectValue(value);
              }}
              list={CreditWalletSearchFilterList}
              selectValue={filterSelectValue}
              textValue={filterText}
              onTextChange={e => {
                setFilterText(e.target.value);
              }}
              onTextSearch={applyFilters}
              onTextClear={() => {
                setFilterText('');
              }}
            />
            <div></div>
            <DateRangePicker onChange={onDateChange} value={dateRange} />
            <FilterSelect
              id="mode"
              label="Mode"
              value={mode}
              options={Object.values(PaymentModeList)}
              onChange={onModeFilterChange}
            />
            <FilterSelect
              id="status"
              label="Type"
              value={status}
              options={Object.values(TypeList)}
              onChange={onStatusFilterChange}
            />
            <SbButton
              className="xs-text ml-1"
              variant="outlined"
              sx={{ borderRadius: '8px !important', padding: '8px !important' }}
              onClick={handleClearFilters}
            >
              Clear Filters
            </SbButton>
          </CreditWalletTableActionContainer>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" fontWeight="700">
              Transaction History
            </Typography>
            <Chip label={`${totalCount} transactions`} color="primary" variant="outlined" />
          </Box>

          {loading ? (
            <Grid container justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
              <CircularProgress color="primary" />
            </Grid>
          ) : transactions.length > 0 ? (
            <ReusableTable
              columns={columns}
              rows={transactions}
              pagination
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={totalCount}
              onPageChange={onPageChange}
              onRowsPerPageChange={onRowsPerPageChange}
              loading={loading}
            />
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No transactions found for the selected period
              </Typography>
            </Paper>
          )}

          {/* Vendor Selection Modal */}
          <VendorSelectionModal
            open={isModalOpen}
            vendors={vendors}
            onClose={() => setIsModalOpen(false)}
            searchTerm={searchTerm}
            onVendorSelect={onVendorSelect}
          />
        </Box>
      </Fade>
    </>
  );
};

export default VendorDetails;
